import React from "react";
import Head from "next/head";
import Header from "./components/Header";
import Footer from "./components/Footer";
import SectionRenderer from "./components/SectionRenderer";
import WhatsappFab from "../../components/WhatsappFab";
import { readArrayFromConfig, DEFAULT_PRE_FOOTER_SECTIONS } from "./utils";

// Liste des familles Google Fonts supportées par l'admin (cf. FormAppearance).
// Pour chaque clé (nom CSS), on précise :
//   - family : nom URL (espaces remplacés par +)
//   - axes   : axes de variation (wght, opsz, ital…) ou null pour wght fixe
// La liste évolue avec FormAppearance, mais reste centralisée ici pour le chargement.
const GOOGLE_FONTS = {
    "Fraunces":             "Fraunces:opsz,ital,wght@9..144,0,500..700;9..144,1,500..700",
    "Inter":                "Inter:wght@400;500;600;700",
    "Manrope":              "Manrope:wght@400;500;600;700;800",
    "Playfair Display":     "Playfair+Display:ital,wght@0,400..900;1,400..900",
    "Cormorant Garamond":   "Cormorant+Garamond:ital,wght@0,400;0,500;0,600;0,700;1,400;1,600",
    "Lora":                 "Lora:ital,wght@0,400..700;1,400..700",
    "DM Serif Display":     "DM+Serif+Display:ital@0;1",
    "Roboto Slab":          "Roboto+Slab:wght@300;400;500;600;700;800",
    "Space Grotesk":        "Space+Grotesk:wght@300..700",
    "Plus Jakarta Sans":    "Plus+Jakarta+Sans:ital,wght@0,400..800;1,400..800",
    "Outfit":               "Outfit:wght@300..800",
    "Nunito Sans":          "Nunito+Sans:ital,opsz,wght@0,6..12,400..900;1,6..12,400..900",
    "Work Sans":            "Work+Sans:ital,wght@0,300..800;1,300..800",
    "DM Sans":              "DM+Sans:ital,opsz,wght@0,9..40,300..1000;1,9..40,300..1000",
    "Source Serif Pro":     "Source+Serif+Pro:ital,wght@0,400..900;1,400..900",
    "Frank Ruhl Libre":     "Frank+Ruhl+Libre:wght@500;600;700", // pour l'hébreu
};

/**
 * Extrait le nom de famille de la première valeur d'une CSS `font-family`.
 *   "Fraunces, ui-serif, Georgia, serif"      → "Fraunces"
 *   "Plus Jakarta Sans, system-ui, sans-serif" → "Plus Jakarta Sans"
 *   "system-ui, -apple-system, sans-serif"    → "system-ui"
 */
function extractFamily(cssFontFamily) {
    if (!cssFontFamily) return null;
    const first = cssFontFamily.split(",")[0].trim().replace(/['"]/g, "");
    return first || null;
}

/**
 * Construit l'URL Google Fonts qui charge les familles utilisées.
 * On y ajoute toujours Frank Ruhl Libre pour l'hébreu.
 * Retourne null si aucune Google Font à charger (uniquement système).
 */
function buildGoogleFontsUrl(displayFamily, bodyFamily) {
    const families = new Set();
    if (displayFamily && GOOGLE_FONTS[displayFamily]) families.add(GOOGLE_FONTS[displayFamily]);
    if (bodyFamily && GOOGLE_FONTS[bodyFamily]) families.add(GOOGLE_FONTS[bodyFamily]);
    // Toujours charger Frank Ruhl Libre pour le rendu hébreu
    families.add(GOOGLE_FONTS["Frank Ruhl Libre"]);
    if (families.size === 0) return null;
    return `https://fonts.googleapis.com/css2?${Array.from(families).map(f => `family=${f}`).join("&")}&display=swap`;
}

/**
 * Wrapper global de toutes les pages du thème 2026.
 *
 *   <Header/>
 *   {children}
 *   {preFooterSections}   ← globales, présentes sur TOUTES les pages
 *   <Footer/>
 *
 * Comportement des sections pré-footer (config["global.preFooterSections"]) :
 *   - Tableau défini (même vide) → on rend exactement ce qu'il contient,
 *     dans l'ordre exact défini par l'admin. La Newsletter est traitée comme
 *     n'importe quelle autre section.
 *   - Tableau absent (admin n'a jamais sauvegardé) → DEFAULT_PRE_FOOTER_SECTIONS
 *     est utilisé (contient Newsletter par défaut, en dernier).
 */
export default function ThemeRoot({ context, children }) {
    const { config } = context;

    // Charge dynamiquement uniquement les Google Fonts effectivement utilisées
    // par l'org (selon ses tokens). Évite de tirer toute la bibliothèque Google
    // Fonts à chaque page, tout en permettant un large choix côté admin.
    // Calculé AVANT la branche embed : les polices relèvent du document, pas du
    // chrome. Les laisser dans le seul retour complet privait de sa typographie
    // toute org servie en embed — y compris sur son propre domaine quand c'est
    // sa configuration qui l'impose (ThemeVars pose --font-body sans que rien
    // ne charge la fonte → repli Georgia/system-ui partout).
    const tokens = config?.theme2026?.tokens || {};
    const displayFamily = extractFamily(tokens.fontDisplay) || "Fraunces";
    const bodyFamily = extractFamily(tokens.fontBody) || "Inter";
    const fontsUrl = buildGoogleFontsUrl(displayFamily, bodyFamily);

    const fontsHead = (
        <Head>
            <link rel="preconnect" href="https://fonts.googleapis.com" />
            <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="" />
            {fontsUrl && <link rel="stylesheet" href={fontsUrl} />}
        </Head>
    );

    if (context.render === "embed") {
        // Mode embed : pas de header/footer/sections globales, juste le contenu.
        // Toutes les classes .tr-* sont scopées .theme-2026 dans 2026.css.
        return (
            <div className="theme-2026 theme-2026--embed">
                {fontsHead}
                {children}
            </div>
        );
    }

    const preFooterSections = readArrayFromConfig(
        config,
        "global.preFooterSections",
        DEFAULT_PRE_FOOTER_SECTIONS
    );

    return (
        <div className="theme-2026">
            {fontsHead}

            <Header context={context} />

            <main className="theme-2026__main">
                {children}
            </main>

            {preFooterSections.length > 0 && (
                <div className="theme-2026__pre-footer">
                    {preFooterSections.map(s => (
                        <SectionRenderer key={s.id} section={s} context={context} />
                    ))}
                </div>
            )}

            <Footer context={context} />

            <WhatsappFab context={context} />
        </div>
    );
}
